/**
 * Helpers para resolver el TEXTO ACTUAL de la sección anclada a un
 * comment (Doc 21 Fase 6 — Nivel A diff).
 *
 * Un comment vive en `(version_id, section_id, instance_key)`. El
 * `instance_key` es path-encoded por `buildInstanceKey` en SectionEditor:
 *
 *   - Root section            → `section_id`
 *   - Inside repeatables      → `casos_de_uso[2].flujos_alternos[1].diagrama`
 *
 * Aquí hacemos el camino inverso: parsear el `instance_key` a `PathSegment[]`
 * y navegar el `content` JSONB con `getValueAtPath`. Devolvemos string
 * legible para los tipos atómicos (rich_text, plain_text, list, kv); en
 * el resto devolvemos `null` para que el caller omita el bloque "actual"
 * (el reviewer hará scroll a la sección manualmente).
 */

import {
  getValueAtPath,
  type PathSegment,
} from '../official-viewer/section_types'

const INDEX_RE = /^(.+)\[(\d+)\]$/

/** Parsea `casos_de_uso[2].flujos_alternos[1].diagrama` → PathSegment[]. */
export function parseInstanceKey(instanceKey: string): PathSegment[] {
  if (!instanceKey) return []
  return instanceKey.split('.').map((segment) => {
    const m = segment.match(INDEX_RE)
    if (m) {
      return { section_id: m[1] as string, index: Number(m[2]) }
    }
    return { section_id: segment }
  })
}

/** Convierte un value arbitrario del JSONB a un string markdown
 *  render-friendly. Cubre los casos comunes; para shapes raros devuelve
 *  null y dejamos que la UI muestre solo el excerpt original. */
export function valueToReadableText(value: unknown): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  // Rich text sections persisten TipTap JSON (`{type: 'doc', content: [...]}`).
  // Lo detectamos ANTES del branch genérico de objeto, así no caemos en
  // el fallback "key: value" que renderizaría literalmente `type: doc`.
  if (isTipTapDoc(value)) {
    return tiptapDocToMarkdown(value as TipTapNode)
  }
  if (Array.isArray(value)) {
    // Listas (bullet_list, numbered_list): join con bullets markdown.
    const items = value
      .map((v) => {
        if (typeof v === 'string') return v.trim()
        if (v && typeof v === 'object') {
          // KV item con campo `text` / `name` / `value` — heurística.
          const obj = v as Record<string, unknown>
          for (const key of ['text', 'name', 'value', 'label']) {
            const val = obj[key]
            if (typeof val === 'string' && val.trim()) return val.trim()
          }
          return null
        }
        return null
      })
      .filter((s): s is string => !!s)
    if (items.length === 0) return null
    return items.map((s) => `- ${s}`).join('\n')
  }
  if (typeof value === 'object') {
    // KV con campos planos: render como "**key**: value" multilínea.
    const obj = value as Record<string, unknown>
    const parts: string[] = []
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v === 'string' && v.trim()) {
        parts.push(`**${k}**: ${v.trim()}`)
      } else if (typeof v === 'number' || typeof v === 'boolean') {
        parts.push(`**${k}**: ${String(v)}`)
      }
    }
    return parts.length > 0 ? parts.join('\n\n') : null
  }
  return null
}

// ─── TipTap doc → markdown ────────────────────────────────────────────
/** Shape mínimo de un nodo TipTap/ProseMirror que necesitamos para
 *  walk y extracción de texto. No queremos importar @tiptap/react aquí
 *  para no inflar el bundle del módulo comments. */
interface TipTapNode {
  type: string
  text?: string
  attrs?: Record<string, unknown>
  content?: TipTapNode[]
  marks?: Array<{ type: string }>
}

function isTipTapDoc(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    (value as { type?: unknown }).type === 'doc' &&
    Array.isArray((value as { content?: unknown }).content)
  )
}

/** Convierte un TipTap doc a markdown plano. Cubre los bloques comunes
 *  (heading, paragraph, bullet/orderedList, codeBlock, blockquote,
 *  horizontal rule, hard break) preservando marks inline básicos
 *  (bold, italic, code). El output va por `marked.parse` en el caller,
 *  así que cualquier markdown válido se renderiza con prose-hiva. */
function tiptapDocToMarkdown(doc: TipTapNode): string | null {
  if (!doc.content || doc.content.length === 0) return null
  const blocks = doc.content
    .map(blockToMarkdown)
    .filter((s): s is string => s !== null && s.length > 0)
  if (blocks.length === 0) return null
  return blocks.join('\n\n').trim() || null
}

function blockToMarkdown(node: TipTapNode): string | null {
  switch (node.type) {
    case 'heading': {
      const level = Math.min(
        6,
        Math.max(1, Number(node.attrs?.['level'] ?? 2)),
      )
      const text = inlineToMarkdown(node.content ?? [])
      return text ? `${'#'.repeat(level)} ${text}` : null
    }
    case 'paragraph': {
      return inlineToMarkdown(node.content ?? []) || null
    }
    case 'bulletList': {
      const items = (node.content ?? [])
        .map((li) => listItemToMarkdown(li, '- '))
        .filter((s): s is string => !!s)
      return items.length > 0 ? items.join('\n') : null
    }
    case 'orderedList': {
      const items = (node.content ?? [])
        .map((li, i) => listItemToMarkdown(li, `${i + 1}. `))
        .filter((s): s is string => !!s)
      return items.length > 0 ? items.join('\n') : null
    }
    case 'codeBlock': {
      const code = inlineToMarkdown(node.content ?? [])
      return code ? `\`\`\`\n${code}\n\`\`\`` : null
    }
    case 'blockquote': {
      const inner = (node.content ?? [])
        .map(blockToMarkdown)
        .filter((s): s is string => !!s)
        .join('\n\n')
      return inner
        ? inner.split('\n').map((line) => `> ${line}`).join('\n')
        : null
    }
    case 'horizontalRule':
      return '---'
    default:
      // Fallback genérico: si tiene content inline, extrae el texto.
      return node.content ? inlineToMarkdown(node.content) || null : null
  }
}

function listItemToMarkdown(item: TipTapNode, prefix: string): string | null {
  const inner = (item.content ?? [])
    .map((n) => {
      if (n.type === 'paragraph') return inlineToMarkdown(n.content ?? [])
      return blockToMarkdown(n)
    })
    .filter((s): s is string => !!s)
    .join(' ')
  return inner ? `${prefix}${inner}` : null
}

function inlineToMarkdown(nodes: TipTapNode[]): string {
  return nodes
    .map((n) => {
      if (n.type === 'text' && typeof n.text === 'string') {
        return applyInlineMarks(n.text, n.marks ?? [])
      }
      if (n.type === 'hardBreak') return '  \n'
      if (n.content) return inlineToMarkdown(n.content)
      return ''
    })
    .join('')
}

function applyInlineMarks(
  text: string,
  marks: Array<{ type: string }>,
): string {
  let out = text
  for (const mark of marks) {
    if (mark.type === 'bold') out = `**${out}**`
    else if (mark.type === 'italic') out = `*${out}*`
    else if (mark.type === 'code') out = `\`${out}\``
    // Otros marks (link, citation, underline, strike) se ignoran en
    // este preview — perdemos el formato pero conservamos el texto,
    // que es lo crítico para comparar contra el excerpt.
  }
  return out
}

/** Resuelve el texto actual de la sección anclada al comment. Devuelve
 *  null si la sección no existe en el content actual o si el shape no
 *  es legible como texto (caller decide qué hacer). */
export function getCommentAnchorText(
  content: Record<string, unknown> | null | undefined,
  instanceKey: string,
): string | null {
  const path = parseInstanceKey(instanceKey)
  if (path.length === 0) return null
  const value = getValueAtPath(content, path)
  return valueToReadableText(value)
}
